import { useId, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';

const W = 1000;
const H = 230;
const pad2 = (h: number) => String(h).padStart(2, '0');

/**
 * גרף שטח של העומס השעתי (hourly_load, 24 ערכים). מציג את חלון שעות הפעילות
 * (לפחות 08–20, ומתרחב אם היו שיחות מחוצה לו), מסמן את שעת השיא, ומציג
 * בועית במעבר עכבר. אין השוואה לתקופה קודמת — ה-API לא מחזיר עומס שעתי של אתמול.
 */
export function HeroChart({ hours }: { hours: number[] }) {
  const gid = useId().replace(/:/g, '');
  const [hover, setHover] = useState<number | null>(null);

  const { from, series, max, peak } = useMemo(() => {
    const nz = hours.map((v, i) => (v > 0 ? i : -1)).filter((i) => i >= 0);
    const from = Math.min(8, nz[0] ?? 8);
    const to = Math.max(20, nz[nz.length - 1] ?? 20);
    const series = hours.slice(from, to + 1);
    const max = Math.max(...series, 1);
    const peak = series.indexOf(Math.max(...series));
    return { from, series, max, peak };
  }, [hours]);

  const n = series.length;
  const x = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * W);
  // מרווח עליון לבועית השיא.
  const y = (v: number) => H - 8 - (v / max) * (H - 48);
  const pts = series.map((v, i) => [x(i), y(v)] as const);

  let line = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const mx = (x0 + x1) / 2;
    line += ` C${mx},${y0} ${mx},${y1} ${x1},${y1}`;
  }
  const area = `${line} L${W},${H} L0,${H} Z`;

  function onMove(e: MouseEvent<HTMLDivElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.round(((e.clientX - r.left) / r.width) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  }

  const leftPct = (i: number) => `${(x(i) / W) * 100}%`;
  const topPct = (i: number) => `${(y(series[i]) / H) * 100}%`;
  const label = (i: number) => `${pad2(from + i)}:00`;
  const showPeak = series[peak] > 0;
  const tx = (i: number) => (i < n * 0.15 ? 'translate(0,-130%)' : i > n * 0.85 ? 'translate(-100%,-130%)' : 'translate(-50%,-130%)');

  return (
    <div>
      <div
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        style={{ position: 'relative', height: H, direction: 'ltr', cursor: 'crosshair' }}
        role="img"
        aria-label={`עומס שעתי, שיא ${series[peak]} שיחות ב-${label(peak)}`}
      >
        <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible', animation: 'gcReveal 1.3s var(--ease-out) both' }}>
          <defs>
            <linearGradient id={`a${gid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" style={{ stopColor: 'var(--accent)', stopOpacity: 0.2 }} />
              <stop offset=".55" style={{ stopColor: 'var(--accent)', stopOpacity: 0.06 }} />
              <stop offset="1" style={{ stopColor: 'var(--accent)', stopOpacity: 0 }} />
            </linearGradient>
          </defs>
          {[57, 115, 172].map((gy) => (
            <line key={gy} x1="0" y1={gy} x2={W} y2={gy} stroke="var(--muted)" strokeOpacity=".18" strokeDasharray="2 5" vectorEffect="non-scaling-stroke" />
          ))}
          <path d={area} fill={`url(#a${gid})`} />
          <path d={line} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>

        {/* נשאר מורכב גם בריחוף (רק מוסתר) כדי שהכניסה לא תתנגן שוב */}
        {showPeak && (
          <div style={{ visibility: hover === null ? 'visible' : 'hidden' }}>
            <div style={{ position: 'absolute', top: topPct(peak), left: leftPct(peak), transform: tx(peak), direction: 'rtl', whiteSpace: 'nowrap', padding: '5px 10px', borderRadius: 7, fontSize: 12, background: 'var(--glass-2)', border: '1px solid var(--glass-line)', boxShadow: 'var(--glass-shadow)', pointerEvents: 'none', animation: 'gcFade .4s ease 1.1s both' }}>
              <b style={{ fontWeight: 600 }}>{series[peak]}</b> · שיא ב-{label(peak)}
            </div>
            <Dot left={leftPct(peak)} top={topPct(peak)} pop />
          </div>
        )}

        {hover !== null && (
          <>
            <div style={{ position: 'absolute', top: -6, bottom: 0, left: leftPct(hover), width: 1, background: 'linear-gradient(180deg,transparent,var(--accent) 20%,var(--accent))', opacity: 0.6, pointerEvents: 'none' }} />
            <Dot left={leftPct(hover)} top={topPct(hover)} />
            <div style={{ position: 'absolute', top: topPct(hover), left: leftPct(hover), transform: tx(hover), direction: 'rtl', minWidth: 120, padding: '8px 12px', borderRadius: 10, background: 'var(--glass-2)', border: '1px solid var(--glass-line)', boxShadow: 'var(--glass-shadow)', pointerEvents: 'none', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{label(hover)}</span>
              <b style={{ fontSize: 13, fontWeight: 600 }}>{series[hover] === 1 ? 'שיחה אחת' : `${series[hover]} שיחות`}</b>
            </div>
          </>
        )}
      </div>
      <div style={{ position: 'relative', height: 16, direction: 'ltr', marginTop: 8 }}>
        {series.map((_, i) => ((from + i) % 2 === 0 ? (
          <span key={i} style={{ position: 'absolute', left: leftPct(i), transform: i === 0 ? 'none' : i === n - 1 ? 'translateX(-100%)' : 'translateX(-50%)', fontSize: 11.5, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
            {pad2(from + i)}
          </span>
        ) : null))}
      </div>
    </div>
  );
}

// pop: נקודת השיא קופצת אחרי שהקו נחשף (gcPop בהשהיה 1.1ש, כמו בעיצוב).
function Dot({ left, top, pop }: { left: string; top: string; pop?: boolean }) {
  return (
    <div style={{ position: 'absolute', left, top, width: 12, height: 12, margin: '-6px 0 0 -6px', borderRadius: 99, background: 'var(--surface)', border: '3px solid var(--accent)', boxShadow: '0 0 0 6px var(--accent-soft)', pointerEvents: 'none', animation: pop ? 'gcPop .5s var(--ease-out) 1.1s both' : undefined }} />
  );
}
